import { areFilesEqual, isImageFile, isPDFFile } from 'utils/fileUtils';

/** Determines if the provided email address is correctly formatted.
 *
 * @param emailAddress - The email address to validate.
 * @returns A boolean indicating whether the email address is correctly formatted.
 */
export const isValidEmailAddress = (emailAddress: string): boolean => {
  const validEmailPattern = /^[A-Z0-9a-z._%+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
  return validEmailPattern.test(emailAddress);
};

// Backend uses this looser pattern: no whitespace or @ on either side of @, with a dot in the domain
export const isValidAssessmentsEmailAddress = (emailAddress: string): boolean => {
  const validEmailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  return validEmailPattern.test(emailAddress);
};

/** Calculates the actual age from a date of birth.
 *
 * @param dateOfBirth - The date of birth string in YYYY-MM-DD format.
 * @returns The calculated age in years, or null if the date is invalid.
 */
const calculateAge = (dateOfBirth: string): number | null => {
  if (!dateOfBirth || typeof dateOfBirth !== 'string') {
    return null;
  }

  const birthDate = new Date(dateOfBirth);

  // Check if the date is valid
  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  const age = today.getUTCFullYear() - birthDate.getUTCFullYear();
  const monthDiff = today.getUTCMonth() - birthDate.getUTCMonth();
  const dayDiff = today.getUTCDate() - birthDate.getUTCDate();

  // Adjust age if birthday hasn't occurred this year
  return monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
};

/** Determines if the provided date of birth is valid.
 *
 * Validation Rules:
 * - Must be a valid date
 * - Cannot be a future date
 * - Cannot be before January 1st, 1900 (reasonable maximum age constraint)
 *
 * @param dateOfBirth - The date of birth string in YYYY-MM-DD format.
 * @returns A boolean indicating whether the date of birth is valid.
 */
export const isValidDateOfBirth = (dateOfBirth: string): boolean => {
  const age = calculateAge(dateOfBirth);

  if (age === null) {
    return false;
  }

  const birthDate = new Date(dateOfBirth);
  const today = new Date();

  // Check if the date is not in the future
  if (birthDate > today) {
    return false;
  }

  // Check reasonable maximum age constraint (prevents ages over ~125 years)
  const EARLIEST_ALLOWED_BIRTH_DATE = new Date('1900-01-01');
  if (birthDate < EARLIEST_ALLOWED_BIRTH_DATE) {
    return false;
  }

  return true;
};

/** Determines if the age calculated from the date of birth meets the minimum age requirement.
 *
 * Validation Rules:
 * - Must be at least the minimum age
 *
 * @param dateOfBirth - The date of birth string in YYYY-MM-DD format.
 * @param minAge - Minimum age required.
 * @returns A boolean indicating whether the age meets the minimum requirement.
 */
export const isValidMinimumAge = (dateOfBirth: string, minAge: number): boolean => {
  // First check if the date of birth is valid (includes minimum birth date constraint)
  if (!isValidDateOfBirth(dateOfBirth)) {
    return false;
  }

  const age = calculateAge(dateOfBirth);

  // This should not happen since isValidDateOfBirth already passed, but adding for safety
  if (age === null) {
    return false;
  }

  // Check minimum age constraint
  return age >= minAge;
};

// File attachment utility functions

/**
 * Checks for duplicate files between the provided files and existing files.
 *
 * @param files - Array of new files to check
 * @param existingFiles - Array of existing files to check against
 * @returns Object containing valid files (non-duplicates) and duplicate files
 */
export const checkForDuplicateFiles = (
  files: File[],
  existingFiles: File[],
): { validFiles: File[]; duplicates: File[] } => {
  const duplicates = files.filter((file) =>
    existingFiles.some((existing) => areFilesEqual(existing, file)),
  );
  const validFiles = files.filter((file) => !duplicates.includes(file));
  return { validFiles, duplicates };
};

export const checkForDuplicateImages = (
  files: File[],
  existingFiles: File[],
): { validFiles: File[]; duplicates: File[] } => {
  const duplicates = files.filter(
    (file) =>
      isImageFile(file.type) && existingFiles.some((existing) => areFilesEqual(existing, file)),
  );
  const validFiles = files.filter((file) => !duplicates.includes(file));
  return { validFiles, duplicates };
};

/**
 * Checks the file sizes of the provided files against a maximum allowed size.
 *
 * @param files - Array of files to check
 * @param maxFileSize - Maximum allowed file size in bytes
 * @returns Object containing valid files and oversized files
 */
export const checkFileSizes = (
  files: File[],
  maxFileSize: number,
): { validFiles: File[]; oversizedFiles: File[] } => {
  const oversizedFiles = files.filter((file) => file.size > maxFileSize && isPDFFile(file.type));
  const validFiles = files.filter((file) => !oversizedFiles.includes(file));
  return { validFiles, oversizedFiles };
};

/** Checks if adding files would exceed the attachment limit.
 *
 * @param files - Array of files to add
 * @param currentAttachmentCount - Current number of attachments
 * @param maxAttachmentCount - Maximum number of attachments allowed
 * @returns Object with valid files (within limit) and whether limit was exceeded
 */
export const checkAttachmentLimit = (
  files: File[],
  currentAttachmentCount: number,
  maxAttachmentCount: number,
): { validFiles: File[]; exceededLimit: boolean } => {
  const availableUploadCount = maxAttachmentCount - currentAttachmentCount;
  const exceededLimit = files.length > availableUploadCount;
  const validFiles = exceededLimit ? files.slice(0, availableUploadCount) : files;
  return { validFiles, exceededLimit };
};
